import { Div, Button, router } from '@codesuma/baseline'
import occasions from '../../../services/occasions'
import customOccasions from '../../../services/custom-occasions'
import countryOccasions, { ICountryOccasion } from '../../../services/country-occasions'
import overrides from '../../../services/occasion-overrides'
import { Source, isPrimary } from '../../../services/day-holidays'
import settings from '../../../services/settings'
import { IDayCell } from '../../../services/calendar'
import { RowMenu, RowMenuButton, closeRowMenu, IRowMenuItem } from './row-menu'
import { RenameDialog } from './rename-dialog'
import style from './index.module.css'

// The day's occasions: Iran's, then any country the user added, then the user's
// own. One block of rows and no heading of its own — it shares a section with
// the day's events, and day-slide/happenings.ts names the two of them.
//
// Every row carries the same dots a task and a note carry. The user's own open
// and delete from there; the rest are lists this app does not own, so what they
// offer is a name of the reader's own and, for the ones that are not days off,
// hiding. Both are kept beside the lists — see services/occasion-overrides, and
// read the note there before widening what hiding may touch.
//
// Once a second country is in play the two are grouped and named, and which one
// leads follows the calendar being read. Someone on the Gregorian grid is
// living by the country they added, and Iran's day is there to know about —
// family, or a call to make. On the Jalaali grid it is the other way round. The
// group that is not being lived by is stepped back rather than hidden, and a
// holiday keeps its colour either way: Nowruz still reads as a holiday in
// Germany, it is just not the day the offices are shut.

// What a repeating occasion says under its title.
const REPEAT_NOTE = {
    jalaali: 'هر سال شمسی',
    gregorian: 'هر سال میلادی',
    hijri: 'هر سال قمری',
}

export const Occasions = (day: IDayCell & { isToday: boolean }) => {
    const base = Div()
    base.addClass(style.occasions)

    const system = settings.get().system
    const foreignFirst = system === 'gregorian'
    // The holiday colour follows the calendar being read, the same way the grid
    // does — see services/day-holidays.ts.
    const holidayClass = (from: Source) =>
        isPrimary(from, system) ? style.occasionHoliday : style.occasionHolidayOther

    // Drawn again after a rename or a hiding, so the row answers the tap it just
    // took rather than waiting for the day to be left and come back to.
    const render = () => {
        base.empty()
        base.style({ display: '' })

        const built = occasions.forDay(day)
        const mine = customOccasions.forDay(day)
        // The countries the user added, from the fetched catalog. Empty until it
        // arrives, and the day page redraws when it does — see
        // services/country-occasions.ts.
        const abroad = countryOccasions.forDay(day)
        if (!built.length && !mine.length && !abroad.length) {
            base.style({ display: 'none' })
            return
        }

        // Every key the reader has put away on this day, so the line that brings
        // them back knows what to bring.
        const hidden: string[] = []

        // One group per country, in the order the catalog handed them over.
        const groups = new Map<string, ICountryOccasion[]>()
        for (const o of abroad) {
            const list = groups.get(o.countryName) ?? []
            list.push(o)
            groups.set(o.countryName, list)
        }
        // Nothing to tell Iran apart from until a country has been added.
        const named = groups.size > 0

        const groupTitle = (text: string) => {
            const title = Div(text)
            title.addClass(style.groupTitle)
            title.attr('dir', 'auto')
            base.append(title)
        }

        // The shape every row takes: what it says, and the dots beside it.
        const row = (options: IRowMenuItem[], label: string, onPick: (action: string) => void) => {
            const wrap = Div()
            wrap.addClass(style.occasion)

            const body = Div()
            body.addClass(style.occasionBody)
            wrap.append(body)

            const more = RowMenuButton(label)
            more.on('click', () => RowMenu(more.el, options).on('pick', onPick))
            wrap.append(more)

            base.append(wrap)
            return { wrap, body }
        }

        // What a list this app does not own can offer. Hiding is missing on a day
        // off on purpose — see the file note above.
        const readOnlyOptions = (renamed: boolean, offDay: boolean): IRowMenuItem[] => [
            { action: 'rename', label: renamed ? 'تغییر نام دلخواه' : 'نام دلخواه' },
            ...(renamed ? [{ action: 'reset', label: 'بازگرداندن نام اصلی' }] : []),
            ...(offDay ? [] : [{ action: 'hide', label: 'پنهان کردن', danger: true }]),
        ]

        const readOnlyPick = (key: string, current: string, original: string) => (action: string) => {
            if (action === 'hide') {
                overrides.hide(key)
                return render()
            }
            if (action === 'reset') {
                overrides.clear(key)
                return render()
            }
            const dialog = RenameDialog(current, original)
            const close = () => dialog.remove()
            dialog.on('save', (text: string) => {
                close()
                overrides.rename(key, text)
                render()
            })
            dialog.on('close', close)
            // Off the body, for the reason the row menu is: in here a fixed
            // element settles against the slider's strip, which is three
            // screens wide, and the panel comes out three screens wide with
            // it. It takes the slide's paper along, since the seasons theme
            // paints each slide its own month's.
            dialog.el.style.setProperty('--paper', getComputedStyle(base.el).getPropertyValue('--paper'))
            document.body.appendChild(dialog.el)
        }

        const iranian = () => {
            const kept = overrides.builtIns(built)
            hidden.push(...kept.hidden)
            if (!kept.shown.length) return
            if (named) groupTitle('ایران')
            for (const o of kept.shown) {
                const { wrap, body } = row(readOnlyOptions(o.title !== o.original, o.isHoliday),
                    `گزینه‌های ${o.title}`, readOnlyPick(o.key, o.title, o.original))
                if (o.isHoliday) wrap.addClass(holidayClass('iran'))
                if (foreignFirst) wrap.addClass(style.aside)
                body.text(o.title)
                body.attr('dir', 'auto')
            }
        }

        const foreign = () => {
            for (const [countryName, list] of groups) {
                const kept = overrides.countries(list)
                hidden.push(...kept.hidden)
                if (!kept.shown.length) continue
                groupTitle(countryName)
                for (const o of kept.shown) {
                    const offDay = o.type === 'public_holiday'
                    const { wrap, body } = row(readOnlyOptions(o.title !== o.original, offDay),
                        `گزینه‌های ${o.title}`, readOnlyPick(o.key, o.title, o.original))
                    if (offDay) wrap.addClass(holidayClass('abroad'))
                    if (!foreignFirst) wrap.addClass(style.aside)

                    const title = Div(o.title)
                    title.attr('dir', 'auto')
                    body.append(title)

                    // The legal date and the day offices close are different days
                    // in the United States, and both are true about this one.
                    if (o.isObserved) {
                        const note = Div('روز تعطیلی رسمی')
                        note.addClass(style.occasionNote)
                        body.append(note)
                    }
                }
            }
        }

        if (foreignFirst) {
            foreign()
            iranian()
        } else {
            iranian()
            foreign()
        }

        // The user's own last, whichever calendar is being read: they are the
        // only ones on the page that can be opened and changed outright.
        if (mine.length && named) groupTitle('رویدادهای شما')
        for (const o of mine) {
            const { wrap, body } = row(
                [
                    { action: 'edit', label: 'ویرایش' },
                    { action: 'remove', label: 'حذف', danger: true },
                ],
                `گزینه‌های ${o.title}`,
                action => {
                    if (action === 'edit') return router.goto(`/event?kind=occasion&id=${o.id}`)
                    customOccasions.remove(o.id)
                    render()
                })
            wrap.addClass(style.mine)

            const title = Div(o.title)
            body.append(title)

            // Which calendar carries it, so a repeating occasion says on the day
            // itself why it comes back — a one-off says nothing.
            if (o.repeat !== 'none') {
                const note = Div(REPEAT_NOTE[o.repeat])
                note.addClass(style.occasionNote)
                body.append(note)
            }
        }

        // What was put away, and the way back. On the day it happened rather than
        // buried in settings: hiding a row is the kind of thing done by accident,
        // and this is where the accident would be noticed.
        if (hidden.length) {
            const restore = Button(`${hidden.length === 1 ? 'یک رویداد' : `${hidden.length} رویداد`} پنهان شده — بازگرداندن`)
            restore.addClass(style.occasionHiddenNote)
            restore.on('click', () => {
                // Spelled out: forEach would hand hide() the index as `on`.
                hidden.forEach(key => overrides.hide(key, false))
                render()
            })
            base.append(restore)
        }
    }

    render()

    // The menu is hung off the body, so it has to be put away with the list
    // rather than left behind when the slide redraws.
    base.on('unmounted', closeRowMenu)

    return base
}
